import { breadcrumbHiddenRoutes, IBreadcrumbWidget } from './entities/breadcrumb.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, effect, inject, Injector, Signal } from '@angular/core';
import { v1 as timestamp, v4 as uuidv4 } from 'uuid';
import { Observable, Subscriber } from 'rxjs';
import { BreadcrumbService } from './application/breadcrumb.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterStore } from '@route/data-access';

export interface IBreadcrumbStore {
  widgets: IBreadcrumbWidget[];
  touched: IBreadcrumbWidget[];
}

export const breadCrumbInitialStore: IBreadcrumbStore = {
  widgets: [],
  touched: [],
};

export const BreadcrumbStore = signalStore(
  { providedIn: 'root' },
  withState(breadCrumbInitialStore),
  withComputed((state: any) => ({
    selectTouchedWidget: computed(() => state.touched()[0]),
  })),
  withMethods((
    state,
    injector = inject(Injector),
    breadcrumbService = inject(BreadcrumbService),
    route = inject(RouterStore),
  ) => {
    const registerWidget = (widget: IBreadcrumbWidget) => {
      const _widget: IBreadcrumbWidget = {
        id: widget.id,
        size: widget.size,
        text: widget.text,
        iconName: widget.iconName,
        css: widget.css,
        iconCss: widget.iconCss,
        flatIcon: widget.flatIcon,
        iconNameTouched: widget.iconNameTouched,
        tooltip: widget.tooltip,
        type: widget?.type,
      }
      patchState(state, { widgets: [_widget, ...state.widgets()] })
    }

    const removeOneWidget = (id: IBreadcrumbWidget['id']) => {
      patchState(state, { widgets: state.widgets().filter((widget) => widget.id !== id) })
    }

    const touchWidget = (widget: IBreadcrumbWidget) => {
      const _widget: IBreadcrumbWidget = {
        id: widget.id,
        size: widget.size,
        timeStamp: String(timestamp()),
        iconName: widget.iconName,
        iconNameTouched: widget.iconNameTouched,
      };
      patchState(state, { touched: [...state.touched(), _widget] })
    }

    const untouchWidget = (widget: IBreadcrumbWidget) => {
      patchState(state, { touched: state.touched().filter((widgets) => widgets.id !== widget.id) })
    }

    const addWidgetObservable = (widget: Partial<IBreadcrumbWidget>): Observable<boolean> => {
      return new Observable((observer: Subscriber<any>) => {
        const _id = uuidv4();
        registerWidget(<IBreadcrumbWidget>{
          id: _id,
          ...widget
        });

        effect(() => {
          const touched = state.touched().find((widget) => widget.id === _id);
          observer.next(!!touched);
        }, {injector})

        return function unsubscribe() {
          removeOneWidget(_id);
        };
      });
    }

    const addWidgetSignals = (widget: Partial<IBreadcrumbWidget>): Signal<boolean> => {
      const _id = uuidv4();
      registerWidget(<IBreadcrumbWidget>{
        id: _id,
        ...widget
      });

      return computed(() => !!state.touched().find((widget) => widget.id === _id))
    }

    return {
      addWidget: (widget: Partial<IBreadcrumbWidget>) => {
        return {
          asObservable: addWidgetObservable(widget),
          asSignal: () => addWidgetSignals(widget),
          removeWidget: () => removeOneWidget(widget.id)
        }
      },
      removeAllWidgets: () => {
        patchState(state, { widgets: [] })
      },
      removeOneWidget: (id: IBreadcrumbWidget['id']) => {
        removeOneWidget(id);
      },
      touchWidget: (widget: IBreadcrumbWidget) => {
        touchWidget(widget);
      },
      unTouchWidget: (widget: IBreadcrumbWidget) => {
        untouchWidget(widget);
      },
      getBreadcrumbs: () => {
        return {
          asObservable: breadcrumbService.breadcrumbs$,
          asSignal: computed(() => toSignal(breadcrumbService.breadcrumbs$))
        };
      },
      isBreadCrumbHidden: ():Signal<boolean> => {
        return computed( () => {
          const routeUrl = route.getCurrentRoute()
          return !!breadcrumbHiddenRoutes.find((item) => routeUrl.includes(item))
        });
      },
      blankState: () => patchState(state, breadCrumbInitialStore),
    }
  })
)
